import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { graphql } from '~/gql'
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { compile as rootCompile } from '~/routes/paths'
import { exists } from '~/utils'

const getByValueDocument = graphql(`
  query UseCreateSharedUrl_GetByValue($value: String) {
    restoringParams(value: $value) {
      key
    }
  }
`)

const getByKeyDocument = graphql(`
  query UseCreateSharedUrl_GetByKey($key: String) {
    restoringParams(key: $key) {
      key
    }
  }
`)

const createDocument = graphql(`
  mutation UseCreateSharedUrl_Create($key: String!, $value: String!) {
    createRestoringParams(key: $key, value: $value) {
      key
    }
  }
`)

type CreateSharedUrlOptions = {
  notCopyToClipboard?: boolean
}

type CreateSharedUrlResult = {
  key: string
  sharedUrl: string
}

export const useCreateSharedUrl = () => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const notify = useNotify()
  const copyToClipboard = useCopyToClipboard()

  const createRestoringParams = React.useCallback(
    async (restoringParams: unknown): Promise<string | null> => {
      const value = JSON.stringify(restoringParams)

      const byValueResult = await graphQLRequest({
        document: getByValueDocument,
        variables: { value },
      })
      const queryResultKey = byValueResult.restoringParams?.key
      if (exists(queryResultKey)) {
        return queryResultKey
      }

      const generateKey = async () => {
        for (let index = 0; index < 10; index++) {
          const key = nanoid(8)
          const byKeyQueryResult = await graphQLRequest({
            document: getByKeyDocument,
            variables: { key },
          })
          const queryResultKey = byKeyQueryResult.restoringParams?.key

          if (!exists(queryResultKey)) {
            return key
          }
        }

        return null
      }

      const key = await generateKey()

      if (exists(key)) {
        await graphQLRequest({
          document: createDocument,
          variables: { key, value },
        })
      }

      return key
    },
    [graphQLRequest],
  )

  return React.useCallback(
    async (
      restoringParams: unknown,
      { notCopyToClipboard = false }: CreateSharedUrlOptions = {},
    ): Promise<CreateSharedUrlResult | null> => {
      const key = await createRestoringParams(restoringParams)
      if (!exists(key)) {
        notify({ content: t('could_not_get_shared_url'), style: 'error' })

        return null
      }

      const sharedUrl = `${window.location.origin}${rootCompile('restore', {
        key,
      })}`

      if (!notCopyToClipboard) {
        copyToClipboard(sharedUrl, {
          notificationText: t('shared_url_copied_to_clipboard'),
        })
      }

      return { key, sharedUrl }
    },
    [copyToClipboard, createRestoringParams, notify, t],
  )
}
